/* eslint-disable react/prop-types */
// 本仓库未把 prop-types 列为直接依赖（全站仅 1 处引用），组件入参契约由 JSDoc 单点维护（同记录页/详情页约定）。
//
// React 默认导入必须保留，但理由不是「JSX 经典运行时」：React 17 下 babel 已是 runtime: 'automatic'，
// 构建产物不需要 React 在作域里；但 eslint plugin:react/recommended 会报「用了 JSX 却没导 React」，
// TS 又把只导不用报成 6133 unused。所以 hooks 从 React 命名空间上取：两边都真正「读到」React。
/**
 * 「生成中」批次的静默轮询 —— 物料生成记录页（契约 §3.2，2026-09-11 接线）
 *
 * 需求背景：从「线下门店物料管理」点「生成二维码物料」创建批次后跳进记录页，
 * 行会一直停在「生成中」，原来必须人肉点「查询」或刷浏览器才更新。
 * 服务端出图是异步任务（§8-A13：最坏 ~30 分钟才收敛），所以前端只能复查，
 * 而复查的全部风险都在于「把等待做成噪音」，故本文件把三条红线写在最前面：
 *
 *   1. 🔴 **复查请求是静默的，绝不用 `actionRef.reload()` 当心跳。**
 *      ProTable 每次 reload 都会过一遍 skeleton，3~5 秒闪一次比不刷新更糟
 *      （pro-table 自带的 `polling` 属性能避免 skeleton，但它每拍都 setState 整份
 *      dataSource，还会在 arm 的瞬间多发一次请求，见 node_modules/@ant-design/pro-table/
 *      lib/useFetchData.js:319；所以这里自己做调度，只借它的思路，不挂它的开关）。
 *      本 hook 的做法：自己拉 → 与上次快照比 `status` → **一致就什么都不做**，
 *      真的变了才让页面 reload 一次（一次批次完成 = 一次可感知的刷新）。
 *   2. 🔴 **只有状态从「生成中」翻到终态才播报。**等待期间任何自动动作都不该打断用户。
 *   3. 🔴 **必须有刹车**：无生成中行即停 / 累计满 `POLL.MAX_MS` 停 / 标签页切走停 /
 *      用户点「停止自动刷新」停 / 组件卸载停。少一条就会变成永动的后台请求。
 *
 * 与「按批次名等 5 秒重查一次」的取舍（当时的结论，留档免得被人再提一遍）：
 *   一次性延时把「多久能好」赌成一个常量，而服务端最坏要 30 分钟；赌输的那一次
 *   toast 已经消失、状态纹丝不动，体验比现在（明确知道要手点）还差。
 *   复查因此按「存在生成中行」触发，与入口无关：创建、补全剩余（补足后回到生成中）、
 *   以及正常翻进来看到别人的生成中批次，走的是同一套代码，不为某个入口特判。
 *
 * 本文件不 import 任何页面，只依赖 constants.js（节奏与文案的单一出口）；
 * 详情页将来要接，直接复用 useGeneratingPolling，把 fetchRows 换成 detail 即可。
 */
import React from 'react';
import { Alert, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { BTN, POLL, POLL_TIP, TEXT, BATCH_STATUS } from './constants';

// hooks 从 React 命名空间上取，原因见文件头注释（让 eslint 与 TS 两边都真正「读到」React）。
const { useEffect, useRef, useState } = React;

/**
 * 轮询状态机（提示条按它渲染，页面只读不写）。
 * IDLE 无生成中行 / RUNNING 复查中 / STOPPED 用户主动停 / TIMEOUT 满 MAX_MS 停 / FAILED 请求失败停。
 */
export const POLL_MODE = {
  IDLE: 'idle',
  RUNNING: 'running',
  STOPPED: 'stopped',
  TIMEOUT: 'timeout',
  FAILED: 'failed',
};

/**
 * 生成中轮询 hook。
 *
 * 所有回调都过一层 `optsRef`，所以调用方可以每帧传新的闭包（箭头函数、内联对象都无所谓），
 * 不会因此重建定时器 —— 定时器只由「有没有生成中的行」和用户的显式动作驱动。
 *
 * @param {Object} options
 * @param {(query:any)=>Promise<Array|null>} options.fetchRows  静默复查；返回 null 表示本次失败（网络或非 00000）
 * @param {()=>any} options.getQuery                            当前表格正在用的查询条件（与表格同源才能比对同一批行）
 * @param {(rows:any[])=>boolean} options.hasGenerating         这批行里是否还有「生成中」
 * @param {(rows:any[])=>Map<string,number>} options.statusSnapshot 行 → (key → status)，key 用批次号
 * @param {(settled:string[],rows:any[])=>void} [options.onResolved] 有批次从生成中翻到终态时播报
 * @param {()=>void} options.onTableRefresh                     真的变了才刷新表格（一次性，非心跳）
 * @param {number} [options.fastMs]        快档间隔，默认 POLL.FAST_INTERVAL_MS
 * @param {number} [options.fastWindowMs]  快档窗口，默认 POLL.FAST_WINDOW_MS
 * @param {number} [options.slowMs]        慢档间隔，默认 POLL.SLOW_INTERVAL_MS
 * @param {number} [options.maxMs]         累计上限，默认 POLL.MAX_MS
 * @return {{mode:string, observe:Function, stop:Function, restart:Function, getWaitedMs:Function, getIntervalMs:Function}}
 */
export const useGeneratingPolling = (options = {}) => {
  const optsRef = useRef(options);
  optsRef.current = options;

  const {
    fastMs = POLL.FAST_INTERVAL_MS,
    fastWindowMs = POLL.FAST_WINDOW_MS,
    slowMs = POLL.SLOW_INTERVAL_MS,
    maxMs = POLL.MAX_MS,
  } = options;

  const [mode, setMode] = useState(POLL_MODE.IDLE);
  /**
   * mode 的同步镜像：调度函数不能在闭包里读 mode（页面可能拿着上一帧的 observe 闭包调进来），
   * 一律读 ref，保证「用户显式停止」不会被旧闭包忽略。
   */
  const modeRef = useRef(POLL_MODE.IDLE);
  /** 待触发的一拍（用 setTimeout 而不是 setInterval：节奏要随窗口递减） */
  const timerRef = useRef(null);
  /** 本轮是否armed（与 mode 分开存：mode 会触发渲染，调度不希望触发渲染） */
  const armedRef = useRef(false);
  /** 在飞标记：慢接口时不让下一拍叠上去 */
  const busyRef = useRef(false);
  /** 本轮起点与「切走累计时长」：等待时长一律按 visible 时间算，切走 10 分钟不该吃掉预算 */
  const startedAtRef = useRef(0);
  const hiddenAtRef = useRef(0);
  const hiddenTotalRef = useRef(0);
  /** 上一次看到的 (批次号 → 状态)，比对基准 */
  const snapshotRef = useRef(new Map());
  /** 本次表格刷新是自己（轮询）触发的：observe 时据此决定要不要重置预算 */
  const selfRefreshRef = useRef(false);
  const unmountedRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  /** 已等待的可计时长（扣除切走的时间），提示条的心跳和超时判定共用它 */
  const getWaitedMs = () =>
    startedAtRef.current
      ? Math.max(Date.now() - startedAtRef.current - hiddenTotalRef.current, 0)
      : 0;

  const setModeSafe = (next) => {
    modeRef.current = next;
    if (unmountedRef.current) {
      return;
    }
    setMode(next);
  };

  /** 停表。nextMode 说明为什么停：IDLE（没得等了）/ STOPPED / TIMEOUT / FAILED */
  const stop = (nextMode) => {
    clearTimer();
    armedRef.current = false;
    busyRef.current = false;
    setModeSafe(nextMode);
  };

  /**
   * 当前这一拍实际用的间隔（与 scheduleNext 选档同一口径，不是常量）。
   * 🔴 提示条得拿它写「每 N 秒刷新」：过了快档窗口仍是5 秒，就是给运营一个假承诺。
   */
  const getIntervalMs = () => (getWaitedMs() < fastWindowMs ? fastMs : slowMs);

  /** 排下一拍：先判超时，再按窗口选快/慢档 */
  const scheduleNext = () => {
    clearTimer();
    if (!armedRef.current || unmountedRef.current) {
      return;
    }
    const waited = getWaitedMs();
    if (waited >= maxMs) {
      // 契约 §3.2：到点必须「挂常驻提示」而不是默默停 —— 生成中还在，只是不再自动查了
      stop(POLL_MODE.TIMEOUT);
      return;
    }
    const delay = getIntervalMs();
    timerRef.current = window.setTimeout(tick, delay);
  };

  /** 复查一拍：静默拉 → 比状态 → 有变化才刷表 + 播报 */
  const tick = async () => {
    timerRef.current = null;
    if (!armedRef.current || unmountedRef.current || busyRef.current) {
      return;
    }
    // 切到后台时这里直接跳过（visibilitychange 已经清了表，兜一层防竞态）
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }
    const {
      fetchRows,
      getQuery,
      hasGenerating,
      statusSnapshot,
      onResolved,
      onTableRefresh,
    } = optsRef.current;
    const query = getQuery ? getQuery() : null;
    if (!query || typeof fetchRows !== 'function') {
      // 表格还没成功加载过（拿不到同源查询），下一拍再试，不做无基准的比对
      scheduleNext();
      return;
    }
    busyRef.current = true;
    let rows = null;
    try {
      rows = await fetchRows(query);
    } catch (error) {
      rows = null;
    }
    busyRef.current = false;
    if (unmountedRef.current || !armedRef.current) {
      return;
    }
    if (!Array.isArray(rows)) {
      // 🔴 失败不清空已有行（契约 §3.2），也不再 5 秒一条错误 toast 刷屏；停成 FAILED 交给用户
      stop(POLL_MODE.FAILED);
      return;
    }
    const snap = statusSnapshot(rows);
    const prev = snapshotRef.current;
    const settled = [];
    let dirty = false;
    prev.forEach((from, key) => {
      if (!snap.has(key)) {
        // 行不在这页了（翻页/筛选变了）：不比，避免把「换了数据集」当成「状态变了」刷成循环
        return;
      }
      const to = snap.get(key);
      if (to === from) {
        return;
      }
      dirty = true;
      if (from === BATCH_STATUS.GENERATING && to !== BATCH_STATUS.GENERATING) {
        settled.push(key);
      }
    });
    snapshotRef.current = snap;
    if (dirty) {
      // 表格刷新会再走一次 observe：打上标记，那一次不许重置预算（否则永远不到 5 分钟）
      selfRefreshRef.current = true;
      if (settled.length && onResolved) {
        onResolved(settled, rows);
      }
      if (onTableRefresh) {
        onTableRefresh();
      }
    }
    if (!hasGenerating(rows)) {
      stop(POLL_MODE.IDLE);
      return;
    }
    scheduleNext();
  };

  /** 起一轮新的预算并排第一拍 */
  const arm = (immediate) => {
    startedAtRef.current = Date.now();
    hiddenTotalRef.current = 0;
    hiddenAtRef.current = 0;
    armedRef.current = true;
    setModeSafe(POLL_MODE.RUNNING);
    if (immediate) {
      tick();
    } else {
      scheduleNext();
    }
  };

  /**
   * 表格每次成功加载后由页面调用（唯一入口）：喂基准快照 + 决定要不要开始等。
   *
   * 预算重置规则（对应「用户动过手就别在他手下抢跑」）：
   *   - 轮询自己触发的刷新 → 沿用剩余预算，只有仍在跑才续拍；
   *   - 用户查询 / 翻页 / 首次带 batchNo 进来 → 重新给满 5 分钟；
   *   - 用户点过「停止自动刷新」后，只有他再手动查询才重新计预算，但**不自动恢复**（尊重显式选择）。
   */
  const observe = (rows) => {
    if (!Array.isArray(rows) || unmountedRef.current) {
      return;
    }
    const { hasGenerating, statusSnapshot } = optsRef.current;
    snapshotRef.current = statusSnapshot(rows);
    if (!hasGenerating(rows)) {
      // 没有生成中的行了：不管之前什么态（含 STOPPED/TIMEOUT）都收条，表格上方不留垃圾
      stop(POLL_MODE.IDLE);
      return;
    }
    if (selfRefreshRef.current) {
      selfRefreshRef.current = false;
      if (armedRef.current && !timerRef.current && !busyRef.current) {
        scheduleNext();
      }
      return;
    }
    if (modeRef.current === POLL_MODE.STOPPED) {
      // 显式停止过：保持不再自动查，但提示条留下（还能点「继续自动刷新」）
      return;
    }
    if (armedRef.current) {
      // 用户手动查询打断了一轮：预算给满，在飞的拍子留着继续
      startedAtRef.current = Date.now();
      hiddenTotalRef.current = 0;
      hiddenAtRef.current = 0;
      return;
    }
    arm(false);
  };

  /** 常驻条上的「继续自动刷新 / 刷新 / 重试」：重给预算并立刻查一次（用户按了就该马上有反应） */
  const restart = () => {
    if (unmountedRef.current) {
      return;
    }
    selfRefreshRef.current = false;
    clearTimer();
    arm(true);
  };

  /** 用户主动停止 */
  const stopByUser = () => stop(POLL_MODE.STOPPED);

  // 标签页切走 → 停表并累计隐藏时长；切回来 → 立刻补查一次（多标签挂着后台轮询是这个方案唯一的真实压力来源）
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        if (!armedRef.current) {
          return;
        }
        clearTimer();
        hiddenAtRef.current = Date.now();
        return;
      }
      if (!hiddenAtRef.current) {
        return;
      }
      hiddenTotalRef.current += Date.now() - hiddenAtRef.current;
      hiddenAtRef.current = 0;
      if (armedRef.current) {
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // 卸载兜底：离开记录页不能让定时器继续打接口
  useEffect(
    () => () => {
      unmountedRef.current = true;
      clearTimer();
      armedRef.current = false;
    },
    [],
  );

  return {
    mode,
    observe,
    stop: stopByUser,
    restart,
    getWaitedMs,
    getIntervalMs,
  };
};

/**
 * 表格上方的常驻提示条（等待中 / 已停止 / 到点 / 失败 四态）。
 *
 * 🔴 秒数是它自己一跳一跳数出来的（1s 本地 state），不是从 hook 里推出来的：
 * 若把「已等待 N 秒」做成 hook 状态，每秒都会重渲染记录页 → 连带重渲染整张 ProTable，
 * 为了一个装饰性的数字让主表跟着抖，代价不值。这里只重渲染这条 Alert。
 *
 * @param {Object} props
 * @param {string} props.mode        POLL_MODE
 * @param {Function} props.onStop    停止自动刷新
 * @param {Function} props.onRestart 继续 / 刷新 / 重试（同一个动作：重给预算 + 立刻查）
 * @param {Function} props.getWaitedMs 已等待毫秒（visible 口径）
 * @param {Function} props.getIntervalMs 当前生效的复查间隔（快/慢档会变，文案不能说死 5 秒）
 */
export const GeneratingPollNotice = (props) => {
  const { mode, onStop, onRestart, getWaitedMs, getIntervalMs } = props;
  const [waitedSec, setWaitedSec] = useState(0);
  // 首帧就取真实间隔（避开 0 兑底），之后随快/慢档在心跳里更新
  const [intervalSec, setIntervalSec] = useState(() =>
    Math.round(
      (getIntervalMs ? getIntervalMs() : POLL.FAST_INTERVAL_MS) / 1000,
    ),
  );
  const running = mode === POLL_MODE.RUNNING;

  useEffect(() => {
    if (!running) {
      return undefined;
    }
    const read = () => {
      setWaitedSec(Math.floor((getWaitedMs ? getWaitedMs() : 0) / 1000));
      setIntervalSec(
        Math.round(
          (getIntervalMs ? getIntervalMs() : POLL.FAST_INTERVAL_MS) / 1000,
        ),
      );
    };
    read();
    const id = window.setInterval(read, 1000);
    return () => window.clearInterval(id);
  }, [running]);

  if (mode === POLL_MODE.IDLE) {
    return null;
  }
  const link = (key, text, onClick) => (
    <Button type="link" size="small" key={key} onClick={onClick}>
      {text}
    </Button>
  );

  if (running) {
    return (
      <Alert
        type="info"
        showIcon
        icon={<LoadingOutlined spin />}
        style={{ margin: '0 24px 16px' }}
        message={POLL_TIP.RUNNING(intervalSec, waitedSec)}
        action={link('stop', BTN.POLL_STOP, onStop)}
      />
    );
  }
  if (mode === POLL_MODE.STOPPED) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ margin: '0 24px 16px' }}
        message={POLL_TIP.STOPPED}
        action={link('resume', BTN.POLL_RESUME, onRestart)}
      />
    );
  }
  if (mode === POLL_MODE.TIMEOUT) {
    // 文案是需求定稿的 TEXT.POLL_TIMEOUT_TIP（「可稍后刷新查看」），一个字都不改
    return (
      <Alert
        type="warning"
        showIcon
        style={{ margin: '0 24px 16px' }}
        message={TEXT.POLL_TIMEOUT_TIP}
        action={link('refresh', BTN.POLL_RETRY_NOW, onRestart)}
      />
    );
  }
  return (
    <Alert
      type="warning"
      showIcon
      style={{ margin: '0 24px 16px' }}
      message={POLL_TIP.FAILED}
      action={link('retry', BTN.POLL_RETRY_NOW, onRestart)}
    />
  );
};
